import type { BridgeCallout, BridgeCalloutOutcome, BridgeCalloutStatus, BridgeHeldBackReason } from '../types.js'

/** "Wed 14 Oct": a shift's date as written, read at noon so no time zone moves it to the day before. */
export function formatShiftDay(date: string): string {
  const parsed = new Date(`${date}T12:00:00`)
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

/** "Wed 14 Oct, 07:00–15:00 · Unit 2-South · CNA". */
export function describeCalloutShift(callout: Pick<BridgeCallout, 'shift'>): string {
  const { shift } = callout
  return `${formatShiftDay(shift.date)}, ${shift.start}–${shift.end} · Unit ${shift.unitName ?? shift.unitId} · ${shift.role}`
}

export function formatInstant(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}

export const STATUS_LABEL: Record<BridgeCalloutStatus, string> = {
  open: 'Open',
  filled: 'Filled',
  expired: 'Expired',
  cancelled: 'Withdrawn',
}

/** Coral for a callout still waiting; green once filled; grey for the rest. */
export const STATUS_CLASS: Record<BridgeCalloutStatus, string> = {
  open: 'bg-coral-lt text-coral',
  filled: 'bg-green/15 text-green',
  expired: 'bg-marine/10 text-ink-mid',
  cancelled: 'bg-marine/10 text-ink-mid',
}

export const HELD_BACK_REASON: Record<BridgeHeldBackReason, string> = {
  overtime: 'would pass 40 hours this week',
  by_administrator: 'held back by the administrator',
}

/** "32 h this week", or what the platform knows when it knows nothing. */
export function describeHours(hours: number | null): string {
  return hours === null ? 'no hours on record this week' : `${hours} h this week`
}

/** "then worked", "then called out" or "then did not show": the partner's report of the shift, once linked. */
const REPORTED: Record<BridgeCalloutOutcome['kind'], string> = {
  completed: 'then worked the shift',
  callout: 'then called out of it',
  no_show: 'then did not show',
}

/** How the callout ended, or how long it has been waiting. */
export function describeOutcome(callout: BridgeCallout): string {
  if (callout.filledBy) {
    const who = callout.pool.find((member) => member.workerId === callout.filledBy?.workerId)?.name ?? callout.filledBy.workerId
    const minutes = Math.round(callout.filledBy.responseSeconds / 60)
    const reported = callout.outcome ? `; ${REPORTED[callout.outcome.kind] ?? 'then reported'}` : ''
    return `${who} accepted ${formatInstant(callout.filledBy.acceptedAt)}, ${minutes < 1 ? 'within a minute' : `${minutes} min`} after the alert${reported}`
  }
  if (callout.status === 'cancelled') {
    return `Withdrawn ${callout.closedAt ? formatInstant(callout.closedAt) : ''}`.trim()
  }
  if (callout.status === 'expired') {
    return `No one accepted before ${formatInstant(callout.expiresAt)}`
  }
  return `Open until ${formatInstant(callout.expiresAt)}`
}
